import { useIonPopover } from '@ionic/react';
import { FullPost, Post } from 'model';
import React from 'react';
import { useHistory } from 'react-router';
import { useDeletePostMutation } from '../api/PostApi';
import { useGetPostReportsQuery } from '../api/ReportApi';
import { useLoggedIn } from '../hooks/useLoggedIn.js';
import { useUserHasRole } from '../hooks/useUserHasRole';
import { RealUserOnly } from '../pages/admin/RealUserOnly';
import { isPostPromoted } from '../util/isPostPromoted.js';
import { CloseComments } from './CloseComments';
import { DeleteItem } from './DeleteItem';
import { HideComments } from './HideComments';
import { IconButton } from './IconButton';
import { PostContentsToHTML } from './PostContentsToHTML';
import { PromotePost } from './PromotePost.js';
import { IconEdit, IconMoreHorizontal } from './QuirkIcons';
import { ReportPost } from './ReportPost';
import { TruncatedSummary } from './TruncatedSummary';

// eslint-disable-next-line css-modules/no-unused-class
import sharedStyles from '../styles/Shared.module.css';
import { useLicenseFeature } from '../hooks/useLiscenseFeature.js';
import { useGetFeedQuery } from '../api/FeedApi.js';

interface MenuProps {
	post: FullPost;
	userid: string;
}
interface MenuPopupProps {
	userid: string;
	post: FullPost;
	hide: { hide: () => void };
	isPromoted: boolean;
	willBePromoted: boolean;
	history?: any;
}

const MenuPopup: React.FC<MenuPopupProps> = ({ userid, post, hide, isPromoted, willBePromoted, history }) => {
	const loggedIn = useLoggedIn();
	const {
		isSuccess,
		data: reportData,
		isFetching,
		isLoading
	} = useGetPostReportsQuery(post._id, { skip: !loggedIn.realUser });
	const admin = useUserHasRole('admin');
	const paymentsEnabled = useLicenseFeature('payments');
	const { refetch } = useGetFeedQuery();

	const editUrl =
		post.postType === 'businessCheckIn' || post.postType === 'businessPost'
			? `/main/latest/bus/${post.businessId}/edit/${post._id}`
			: `/main/latest/${post.postType}/edit/${post._id}`;
	const onDeletePost = (): void => {
		// Update the feed
		refetch();
		history.push('/main/latest');
	};
	return (
		<div className={sharedStyles.popMenu}>
			{userid === post.author && !isPromoted && !willBePromoted && paymentsEnabled && (
				<PromotePost post={post} hideTooltip={hide.hide} />
			)}
			{userid !== post.author && (
				<ReportPost
					disabled={!isSuccess || isFetching || isLoading}
					post={post}
					isReported={!!reportData?._id}
					hideTooltip={hide.hide}
				/>
			)}
			{userid === post.author && (
				<IconButton
					label="Edit"
					testId="EditPostButton"
					icon={<IconEdit />}
					onClick={() => {
						hide.hide();
						history.push(editUrl);
					}}
				/>
			)}

			{(userid === post.author || admin) && (
				<DeleteItem
					item={post as unknown as Post}
					hideTooltip={hide.hide}
					actionLabel="Delete"
					dismissLabel="Cancel"
					mutation={useDeletePostMutation}
					landingPage={onDeletePost}
					testId="DeletePostButton"
					modalContent={
						<>
							<h4 style={{ marginBottom: '40px' }}>Are you sure you want to delete this post?</h4>

							<TruncatedSummary>
								<PostContentsToHTML>{post?.contents}</PostContentsToHTML>
							</TruncatedSummary>
						</>
					}
				/>
			)}
			{userid === post.author && <HideComments post={post} action={hide.hide} />}
			{userid === post.author && post.displayComments && <CloseComments post={post} action={hide.hide} />}
		</div>
	);
};

export const PostMenu: React.FC<MenuProps> = ({ post, userid }) => {
	const history = useHistory();

	const { isPromoted, willBePromoted } = isPostPromoted({
		startDate: post?.promotionStartDate,
		endDate: post?.promotionEndDate
	});

	const [show, hide] = useIonPopover(
		<MenuPopup
			post={post}
			userid={userid}
			hide={{ hide: () => hide() }}
			isPromoted={isPromoted}
			willBePromoted={willBePromoted}
			history={history}
		/>,
		{}
	);

	return (
		<RealUserOnly>
			<div className={sharedStyles.menuIconContainer} data-testid={`PostMenu-${post._id}`}>
				<IconButton icon={<IconMoreHorizontal />} onClick={(e: any) => show({ event: e })} />
			</div>
		</RealUserOnly>
	);
};
